import moment from "moment-timezone";
import type { Message } from "../types/messaging";

/**
 * Clusters consecutive chat messages so a rapid back-to-back burst reads as one
 * cohesive block instead of a stack of individually-timestamped bubbles.
 *
 * A message joins the previous one's cluster when it is from the **same side**
 * (inbound/outbound — they render on opposite edges, so a reply always starts a
 * new cluster) and was sent **within `windowMs`** of it. Only the last bubble of
 * a cluster shows the time/status meta, and it carries the cluster's most recent
 * send time (messages arrive oldest→newest, so the tail is the most recent).
 *
 * Pure and framework-free so it is trivial to unit-test; `created_dt` is a naive
 * UTC string from the API, parsed as UTC for the gap math.
 */
export const CLUSTER_WINDOW_MS = 2 * 60 * 1000; // 2 minutes

export interface MessageClusterFlags {
  /** Last message of its cluster — show the (most recent) time/status here. */
  showMeta: boolean;
  /** First message of a new cluster — gets extra top spacing from the prior one. */
  isClusterStart: boolean;
}

function sameCluster(a: Message, b: Message, windowMs: number): boolean {
  if (a.direction !== b.direction) return false;
  const gap = Math.abs(
    moment.utc(b.created_dt).valueOf() - moment.utc(a.created_dt).valueOf(),
  );
  return gap <= windowMs;
}

/**
 * Map each message (given oldest→newest) to its display flags. Callers apply
 * these per day bucket, which naturally keeps clusters from spanning a day
 * divider.
 */
export function computeMessageClusterFlags(
  messages: Message[],
  windowMs: number = CLUSTER_WINDOW_MS,
): MessageClusterFlags[] {
  return messages.map((message, i) => {
    const prev = messages[i - 1];
    const next = messages[i + 1];
    return {
      isClusterStart: !prev || !sameCluster(prev, message, windowMs),
      showMeta: !next || !sameCluster(message, next, windowMs),
    };
  });
}
